<template>
  <b-container class="text-center py-4 twofa-container">
    <div class="mb-3">
      <b-icon
        icon="shield-lock"
        variant="primary"
        font-scale="4"
        aria-hidden="true"
      ></b-icon>
    </div>

    <h4 class="font-weight-bold mb-2">Authenticate Your Account</h4>

    <p>
      Please verify your account by entering the authorization code sent to your
      email <strong>{{ loginCodeUser.Email }}</strong>.
    </p>

    <b-row class="justify-content-center my-4">
      <b-col cols="2" md="1" v-for="n in 6" :key="n">
        <b-form-input
          v-model="codeDigits[n - 1]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="text-center otp-input color-primary"
          :id="`input-${n}`"
          @paste="onPaste"
          :autofocus="n === 1"
          @input="onChange(n)"
          @keydown.native="onKeydown"
          :disabled="isLoading"
        ></b-form-input>
      </b-col>
    </b-row>

    <div class="d-flex flex-column align-items-center">
      <!-- Submit button (fallback manual submit) -->
      <b-button
        variant="info"
        class="background-color-primary rounded-border w-50"
        @click="onCheckCode"
        :disabled="isLoading"
      >
        Verify
      </b-button>
      <div class="mt-5">
        <p v-if="resendSeconds > 0" class="text-muted mb-0">
          Didn't receive email? Resend code in {{ resendSeconds }} seconds
        </p>

        <!-- Resend button (only visible after countdown hits 0) -->
        <b-button
          v-else
          variant="outline-primary"
          class="border border-primary"
          pill
          :disabled="isLoading"
          @click="handleResendClick"
        >
          Resend a new code
        </b-button>
      </div>
      <b-button
        variant="link"
        class="mt-3 d-flex align-items-center mt-2"
        @click="$router.push('/login')"
      >
        <b-icon icon="arrow-left" font-scale="1" class="mr-2"></b-icon>
        Back to Login
      </b-button>
    </div>
  </b-container>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import Loading from '@/components/utility/loading.vue'
import { User } from '@/store/security/types'
import { isMobile, handleResize } from '@/common/helpers/responsive-helper'
import { TPIException } from '@/store/editor/types'
import MxSecurity from '@/mixins/security'
import { namespace } from 'vuex-class'

const nsSecurity = namespace('security')

@Component({
  components: {
    Loading,
  },
})
export default class extends Mixins(MxSecurity) {
  @nsSecurity.State('loginCodeUser')
  public loginCodeUser!: User

  private codeDigits: string[] = ['', '', '', '', '', '']

  private isLoading = false

  public isMobile = isMobile()
  private resendSeconds: number = 300
  private resendTimer: number | null = null
  public created() {
    handleResize((isMobile) => {
      this.isMobile = isMobile
    })
    this.startResendTimer() // start countdown on load
  }

  private onCheckCode() {
    this.isLoading = true
    const { $router } = this
    const code = this.codeDigits.join('') // Join all 6 digits
    const acceptanceDone = this.loginCodeUser.AcceptanceDone
    this.checkCode({ code })
      .then(() => {
        if (!acceptanceDone) {
          $router.push({ name: 'login', params: { checkAcceptance: '1' } }).catch(() => { })
          return
        }
        $router.push('/').catch(() => {})
      })
      .catch(({ Message }: TPIException) => {
        this.toastError(Message)
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  private onResendEmail() {
    this.isLoading = true

    this.resendEmail()
      .then(() => {
        this.toastSuccess('A new code was sent to your email')
      })
      .catch(({ Message }: TPIException) => {
        this.toastError(Message)
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  public beforeDestroy() {
    this.clearResendTimer()
  }

  private startResendTimer() {
    this.clearResendTimer()
    this.resendSeconds = 300
    this.resendTimer = window.setInterval(() => {
      if (this.resendSeconds > 0) {
        this.resendSeconds--
      }
      if (this.resendSeconds === 0) {
        this.clearResendTimer()
      }
    }, 1000)
  }

  private clearResendTimer() {
    if (this.resendTimer !== null) {
      clearInterval(this.resendTimer)
      this.resendTimer = null
    }
  }

  private handleResendClick() {
    this.startResendTimer()
    this.onResendEmail()
  }

  private onKeydown(evt: KeyboardEvent) {
    const allowed =
      ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
        evt.key,
      ) ||
      /^[0-9]$/.test(evt.key) ||
      // allow Ctrl+V / Cmd+V
      ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === 'v')

    if (!allowed) {
      evt.preventDefault()
    }
  }

  public onChange(idx) {
    const el = document.getElementById('input-' + (idx + 1))

    if (this.codeDigits.join('').length === 6) {
      this.onCheckCode()
    } else if (el) {
      el?.focus()
    }
  }

  public onPaste(e) {
    e.preventDefault()
    // Figure out which input the user pasted into (expects id="input-1"..."input-6")
    const target = e.target as HTMLElement
    const match = target?.id?.match(/^input-(\d)$/)
    const startIndex = match
      ? Math.min(5, Math.max(0, parseInt(match[1], 10) - 1))
      : 0

    // Get clipboard text, keep only digits, and cap to available boxes
    const raw = e.clipboardData?.getData('text/plain') || ''
    const digits = raw
      .replace(/\D/g, '')
      .slice(0, 6 - startIndex)
      .split('')
    if (digits.length === 0) return

    // Distribute across codeDigits from the start index
    for (let i = 0; i < digits.length; i++) {
      const idx = startIndex + i
      if (idx > 5) break
      this.$set(this.codeDigits, idx, digits[i])
    }

    // If all 6 filled -> auto submit, else focus the next empty box
    if (this.codeDigits.filter((d) => d !== '').length === 6) {
      this.$nextTick(() => {
        this.onCheckCode()
        this.focusInputByIndex(5)
      })
    } else {
      const nextEmpty = this.codeDigits.findIndex((d) => d === '')
      this.focusInputByIndex(
        nextEmpty === -1 ? 5 : Math.max(startIndex, nextEmpty),
      )
    }
  }

  private focusInputByIndex(index: number) {
    const el = document.getElementById(
      'input-' + (index + 1),
    ) as HTMLInputElement | null
    if (el) {
      el.focus()
      try {
        el.setSelectionRange(0, el.value.length)
      } catch {}
    }
  }
}

</script>
<style scoped>
.twofa-container {
  height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.otp-input {
  font-size: 1.5rem;
  height: 3rem;
  max-width: 3rem;
  margin: 0 auto;
  text-align: center;
  border-radius: 0.5rem;
  font-weight: bold;
  padding: 0;
}

@media (max-width: 576px) {
  .otp-input {
    font-size: 1.2rem;
    height: 2.5rem;
    max-width: 2.2rem;
  }

  .twofa-container {
    height: 100%;
  }
}
</style>
